import {
  CalendarIcon,
  ChatAlt2Icon,
  CursorClickIcon,
  ExclamationCircleIcon,
  GiftIcon,
  GlobeAltIcon,
  IdentificationIcon,
  LightningBoltIcon,
  MicrophoneIcon,
  RefreshIcon,
  ViewGridIcon,
} from '@heroicons/react/outline';

import { useWebsiteFaqs } from './websiteShared';
import BubbleStyles from '../mocks/BubbleStyles';
import SiteChatDemo, { ChatButton } from '../mocks/SiteChatDemo';
import WordPressPlugin from '../mocks/WordPressPlugin';
import type { ChannelContent, DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';

const BRAND: DemoBrand = {
  name: 'Harbor Yoga',
  initial: 'H',
  bg: 'bg-[#0f766e]',
};

/*
 * Facts this page relies on (checked 2026-10-21, docs/agents/website.mdx,
 * website-platforms.mdx, appearance.mdx, voice.mdx): the website chat works
 * on every plan; the WordPress plugin "Easy-Peasy.AI Chatbot" takes the
 * Agent ID, a corner, a button colour and an icon URL; WordPress.com needs a
 * plan that allows plugins; Appearance changes reach the site within about
 * a minute and win over older install settings; voice calls in the widget
 * are Unlimited 200+ (Playground trial otherwise); actions are paid.
 */
export function useWordPressContent(): ChannelContent {
  const { t } = useChannelsT();
  const websiteFaqs = useWebsiteFaqs();

  return {
    key: 'wordpress',
    name: 'WordPress',
    logo: 'FaWordpress',
    seoTitle: t(
      'WordPress AI Chatbot: Add an AI Agent to Your Site With a Plugin',
    ),
    seoDescription: t(
      'Add an AI chatbot to WordPress with a free plugin. It answers visitors from your own content, captures leads and books appointments. Works on the Free plan.',
    ),
    hero: {
      label: t('WordPress AI chatbot'),
      title: t('Your WordPress site, now with answers.'),
      accent: t('Added with one free plugin.'),
      lead: t(
        'Install the Easy-Peasy.AI Chatbot plugin, paste your agent’s ID, and a chat bubble trained on your own content answers visitors on every page.',
      ),
      facts: [
        t('Works on the Free plan'),
        t('No theme editing'),
        t('Your colors and icon'),
      ],
      description: t(
        'Example: a visitor on a yoga studio’s WordPress site asks about beginner classes at the weekend. The agent answers from the timetable and shows a Book a class button.',
      ),
      demo: (
        <SiteChatDemo
          brand={BRAND}
          url='harboryoga.wordpress.com'
          site={{
            headline: t('Find your calm, one breath at a time.'),
            picture:
              'bg-[linear-gradient(135deg,#5eead4_0%,#0f766e_55%,#134e4a_100%)]',
            accent: 'text-[#0f766e] dark:text-[#5eead4]',
          }}
          lines={[
            {
              from: 'visitor',
              text: t('Do you have beginner classes on weekends?'),
              at: 700,
            },
            {
              from: 'agent',
              content: t(
                'Yes! Gentle Flow runs on Saturdays at 9:00 and Sundays at 10:00, and your first class is free.',
              ),
              typingAt: 1200,
              at: 2100,
            },
            {
              from: 'agent',
              content: <ChatButton brand={BRAND} label={t('Book a class')} />,
              at: 2700,
            },
          ]}
        />
      ),
      wide: true,
      steps: [],
    },
    jobs: {
      title: t('Turns visitors into customers.'),
      tail: t('Even when you’re offline.'),
      lead: t(
        'Visitors leave when they can’t find an answer. Your agent knows your pages, prices and policies, and answers in seconds, on every page.',
      ),
      items: [
        {
          icon: ChatAlt2Icon,
          title: t('Answers questions'),
          detail: t(
            'Opening hours, prices, services and policies, straight from your website and documents.',
          ),
        },
        {
          icon: CursorClickIcon,
          title: t('Shows buttons'),
          detail: t(
            'Sends visitors to booking, pricing or checkout pages with a button in the chat.',
          ),
        },
        {
          icon: IdentificationIcon,
          title: t('Captures leads'),
          detail: t(
            'Collects names, emails and anything else you ask for, one question at a time, and saves them to Leads.',
          ),
        },
        {
          icon: CalendarIcon,
          title: t('Books appointments'),
          detail: t(
            'Checks when you’re free and books the time in your Google Calendar.',
          ),
        },
        {
          icon: GlobeAltIcon,
          title: t('Speaks their language'),
          detail: t(
            'Replies in the language each customer writes in, from the same knowledge.',
          ),
        },
        {
          icon: MicrophoneIcon,
          title: t('Talks out loud'),
          detail: t(
            'On Unlimited 200 and up, visitors can talk to it by voice right in the chat.',
          ),
        },
      ],
    },
    setup: {
      title: t('Install the plugin, paste one ID.'),
      tail: t('No code needed.'),
      steps: [
        {
          title: t('Install the plugin'),
          detail: t(
            'In WordPress, go to Plugins, then Add New, search for Easy-Peasy.AI Chatbot, and click Install Now and Activate.',
          ),
        },
        {
          title: t('Copy your Agent ID'),
          detail: t(
            'In Easy-Peasy.AI, open your agent, go to Channels, then Chat bubble, click Manage and copy Your Agent ID.',
          ),
        },
        {
          title: t('Paste and save'),
          detail: t(
            'In WordPress, open Settings, then Easy-Peasy.AI Bot, paste the ID, pick a corner and a color, and click Save Settings.',
          ),
        },
      ],
      guide: 'https://easy-peasy.ai/docs/agents/website-platforms#wordpress',
      guideLabel: t('Read the WordPress setup guide'),
      description: t(
        'WordPress admin showing the Easy-Peasy.AI Bot settings with the agent ID, the bottom right position and a teal button color, next to a note that the plugin is active.',
      ),
      visual: <WordPressPlugin color={BRAND.bg} />,
    },
    spotlight: {
      title: t('Make the bubble yours.'),
      tail: t('No code, no redeploy.'),
      lead: t(
        'Pick where the bubble sits, its icon, its color and a short label, and write the greeting that pops up beside it. Your site updates within about a minute.',
      ),
      points: [
        t('A corner, or a tab on the side of the page'),
        t('Built-in icons, or upload your own'),
        t('A greeting that pops up next to the bubble'),
        t('Hide “Powered by” on Unlimited 200 and up'),
      ],
      description: t(
        'The chat bubble settings with the bottom right corner, the spark icon and a teal color chosen, next to the bubble with its label and greeting as visitors see them.',
      ),
      visual: (
        <BubbleStyles
          brand={BRAND}
          greeting={t('👋 New to yoga? Ask me anything about our classes.')}
        />
      ),
    },
    rules: {
      title: t('Good to know.'),
      tail: t('WordPress, in plain words.'),
      items: [
        {
          icon: GiftIcon,
          title: t('Free plan included'),
          detail: t(
            'The website chat works on every plan, including Free. Actions, such as buttons and bookings, need a paid plan.',
          ),
        },
        {
          icon: ExclamationCircleIcon,
          title: t('WordPress.com plans'),
          detail: t(
            'WordPress.com sites need a plan that allows plugins. On other plans, put the chat inside a page with a Custom HTML block.',
          ),
        },
        {
          icon: LightningBoltIcon,
          title: t('Doesn’t slow your site'),
          detail: t(
            'The bubble shows right away, and the chat itself loads once the page has settled.',
          ),
        },
        {
          icon: RefreshIcon,
          title: t('Changes go live on their own'),
          detail: t(
            'Change the bubble’s look or greeting in Appearance and your site updates within about a minute, with no new code.',
          ),
        },
        {
          icon: ViewGridIcon,
          title: t('More than four corners'),
          detail: t(
            'The plugin offers the four corners. For a side tab or a wider chat, paste the install snippet instead.',
          ),
        },
        {
          icon: MicrophoneIcon,
          title: t('Voice on Unlimited 200 and up'),
          detail: t(
            'Visitors can talk to your agent by voice on Unlimited 200 and up. On other plans, try voice in the Playground.',
          ),
        },
      ],
    },
    faqs: [
      {
        question: t('How do I add an AI chatbot to my WordPress site?'),
        answer: t(
          'Install the free Easy-Peasy.AI Chatbot plugin from Plugins, Add New, then paste your agent’s ID in Settings, Easy-Peasy.AI Bot. The chat bubble appears on every page.',
        ),
      },
      {
        question: t('Does it work with WordPress.com?'),
        answer: t(
          'Yes, on a WordPress.com plan that allows plugins. On other plans, put the chat inside a page with a Custom HTML block and the iframe code.',
        ),
      },
      ...websiteFaqs,
    ],
    finalTitle: t('Your WordPress site could be answering by lunchtime.'),
    finalExchange: {
      question: t('Is there parking at the studio?'),
      answer: t('Yes, free parking right behind the building.'),
    },
  };
}
